import { useMemo, useState } from 'react';
import { Popconfirm } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FilterChips, StatusPill } from '../../components';
import { useDeleteTemplate, useTemplatesQuery } from '../../queries/templates';
import { CATEGORY_META } from './constants';
import TemplateModal from './TemplateModal';
import TemplateText from './TemplateText';
import {
  Card,
  IconBtn,
  LinkBtn,
  ListEmpty,
  Muted,
  PrimaryBtn,
  TplBody,
  TplFilters,
  TplFoot,
  TplGrid,
  TplHead,
  TplItem,
  TplTop
} from './styled';

const isMine = (t, userId) => String(t.createdByUserId) === String(userId);

const FILTERS = {
  all: () => true,
  mine: (t, userId) => isMine(t, userId),
  team: (t) => t.scope === 'team'
};

export default function TemplatesTab({ currentUserId, canManage, onUse }) {
  const { data: templates = [], isLoading } = useTemplatesQuery();
  const remove = useDeleteTemplate();
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);

  const options = useMemo(
    () =>
      Object.keys(FILTERS).map((key) => ({
        key,
        label: `${key[0].toUpperCase()}${key.slice(1)} (${templates.filter((t) => FILTERS[key](t, currentUserId)).length})`
      })),
    [templates, currentUserId]
  );
  const visible = templates.filter((t) => FILTERS[filter](t, currentUserId));

  return (
    <Card aria-label="Message templates">
      <TplHead>
        <p>
          Reusable SMS for leasing, maintenance, and owner updates. Variables like <code>{'{{first_name}}'}</code> fill
          in when you send.
        </p>
        {canManage && (
          <PrimaryBtn type="button" onClick={() => setEditing({})}>
            <FontAwesomeIcon icon={faPlus} />
            Add template
          </PrimaryBtn>
        )}
      </TplHead>
      <TplFilters>
        <FilterChips bare label="Template owner" options={options} value={filter} onChange={setFilter} />
      </TplFilters>

      {isLoading ? (
        <ListEmpty>Loading templates…</ListEmpty>
      ) : visible.length === 0 ? (
        <ListEmpty>No templates in this view.</ListEmpty>
      ) : (
        <TplGrid>
          {visible.map((t) => {
            const meta = CATEGORY_META[t.category] || CATEGORY_META.general;
            return (
              <TplItem key={t._id}>
                <TplTop>
                  <h3>{t.title}</h3>
                  <StatusPill color={meta.color}>{meta.label}</StatusPill>
                  <Muted $small $push>
                    Used {t.usageCount || 0}×
                  </Muted>
                </TplTop>
                <TplBody>
                  <TemplateText text={t.body} />
                </TplBody>
                <TplFoot>
                  <LinkBtn type="button" onClick={() => onUse(t)}>
                    Use in a message
                  </LinkBtn>
                  <Muted>{t.scope === 'team' ? 'Team' : isMine(t, currentUserId) ? 'Mine' : 'Personal'}</Muted>
                  {canManage && (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                      <IconBtn type="button" aria-label={`Edit ${t.title}`} onClick={() => setEditing(t)}>
                        <FontAwesomeIcon icon={faPen} />
                      </IconBtn>
                      <Popconfirm title="Delete this template?" okText="Delete" onConfirm={() => remove.mutate(t._id)}>
                        <IconBtn type="button" $danger aria-label={`Delete ${t.title}`}>
                          <FontAwesomeIcon icon={faTrash} />
                        </IconBtn>
                      </Popconfirm>
                    </span>
                  )}
                </TplFoot>
              </TplItem>
            );
          })}
        </TplGrid>
      )}

      {editing && <TemplateModal template={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}
